import { SpellType } from '../game/CooldownManager';
import { WizardType } from '../wizards/WizardTypes';

export interface AchievementDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'combat' | 'mastery' | 'milestone';
  maxProgress: number;
  hidden?: boolean;
}

export interface AchievementProgress {
  unlocked: boolean;
  unlockedAt: number | null;
  progress: number;
}

export interface PlayerStats {
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  currentWinStreak: number;
  bestWinStreak: number;
  totalSpellsCast: number;
  totalGestureSpellsCast: number;
  totalCombosTrigered: number;
  totalShieldsBlocked: number;
  winsPerClass: Record<WizardType, number>;
  spellsCastPerType: Record<SpellType, number>;
}

export type AchievementEventType =
  | 'match_end'
  | 'spell_cast'
  | 'combo_triggered'
  | 'shield_blocked'
  | 'gesture_spell_cast';

export interface AchievementEvent {
  type: AchievementEventType;
  data: {
    won?: boolean;
    playerHealth?: number;
    enemyHealth?: number;
    damageTaken?: number;
    spellType?: SpellType;
    wizardType?: WizardType;
    isGesture?: boolean;
    spellsCastThisMatch?: Set<SpellType>;
  };
}

const ACHIEVEMENT_DEFINITIONS: AchievementDefinition[] = [
  {
    id: 'first_blood',
    name: 'First Blood',
    description: 'Win your first match',
    icon: 'crossed_swords',
    category: 'milestone',
    maxProgress: 1,
  },
  {
    id: 'gesture_mastery',
    name: 'Gesture Mastery',
    description: 'Cast 100 spells using webcam gestures',
    icon: 'hand',
    category: 'mastery',
    maxProgress: 100,
  },
  {
    id: 'combo_king',
    name: 'Combo King',
    description: 'Trigger 50 named combos',
    icon: 'fire',
    category: 'combat',
    maxProgress: 50,
  },
  {
    id: 'shield_master',
    name: 'Shield Master',
    description: 'Block 100 incoming spells with shields',
    icon: 'shield',
    category: 'combat',
    maxProgress: 100,
  },
  {
    id: 'underdog',
    name: 'Underdog',
    description: 'Win a match with less than 10 HP remaining',
    icon: 'heart',
    category: 'combat',
    maxProgress: 1,
  },
  {
    id: 'variety_show',
    name: 'Variety Show',
    description: 'Cast all 5 spells in a single match',
    icon: 'sparkles',
    category: 'mastery',
    maxProgress: 1,
  },
  {
    id: 'untouchable',
    name: 'Untouchable',
    description: 'Win a match without taking damage',
    icon: 'star',
    category: 'combat',
    maxProgress: 1,
  },
  {
    id: 'hot_streak',
    name: 'Hot Streak',
    description: 'Win 5 matches in a row',
    icon: 'flame',
    category: 'milestone',
    maxProgress: 5,
  },
  {
    id: 'dedicated',
    name: 'Dedicated',
    description: 'Play 50 total matches',
    icon: 'trophy',
    category: 'milestone',
    maxProgress: 50,
  },
  {
    id: 'class_expert',
    name: 'Class Expert',
    description: 'Win 10 matches with each wizard class',
    icon: 'crown',
    category: 'mastery',
    maxProgress: 4, // 4 classes
  },
];

const STORAGE_KEY_STATS = 'wizard_duel_player_stats';
const STORAGE_KEY_ACHIEVEMENTS = 'wizard_duel_achievements';

export class AchievementManager {
  private stats: PlayerStats;
  private achievements: Map<string, AchievementProgress>;
  private definitions: Map<string, AchievementDefinition>;
  private unlockListeners: Set<(achievement: AchievementDefinition) => void> = new Set();

  constructor() {
    this.definitions = new Map();
    for (const def of ACHIEVEMENT_DEFINITIONS) {
      this.definitions.set(def.id, def);
    }

    this.stats = this.loadStats();
    this.achievements = this.loadAchievements();
  }

  private getDefaultStats(): PlayerStats {
    return {
      totalMatches: 0,
      totalWins: 0,
      totalLosses: 0,
      currentWinStreak: 0,
      bestWinStreak: 0,
      totalSpellsCast: 0,
      totalGestureSpellsCast: 0,
      totalCombosTrigered: 0,
      totalShieldsBlocked: 0,
      winsPerClass: {
        pyromancer: 0,
        frostmage: 0,
        archmage: 0,
        battlemage: 0,
        necromancer: 0,
        druid: 0,
        chronomancer: 0,
      },
      spellsCastPerType: {
        fireball: 0,
        iceShard: 0,
        shield: 0,
        lightningBolt: 0,
        healSpell: 0,
        reflect: 0,
        teleport: 0,
        meteor: 0,
        superShield: 0,
        blizzard: 0,
      },
    };
  }

  private loadStats(): PlayerStats {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_STATS);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Merge with defaults to handle new fields
        return { ...this.getDefaultStats(), ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load player stats:', e);
    }
    return this.getDefaultStats();
  }

  private saveStats(): void {
    try {
      localStorage.setItem(STORAGE_KEY_STATS, JSON.stringify(this.stats));
    } catch (e) {
      console.warn('Failed to save player stats:', e);
    }
  }

  private loadAchievements(): Map<string, AchievementProgress> {
    const map = new Map<string, AchievementProgress>();
    // Initialize all achievements
    for (const def of ACHIEVEMENT_DEFINITIONS) {
      map.set(def.id, { unlocked: false, unlockedAt: null, progress: 0 });
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY_ACHIEVEMENTS);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, AchievementProgress>;
        for (const [id, progress] of Object.entries(parsed)) {
          if (map.has(id)) {
            map.set(id, progress);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load achievements:', e);
    }
    return map;
  }

  private saveAchievements(): void {
    try {
      const obj: Record<string, AchievementProgress> = {};
      for (const [id, progress] of this.achievements.entries()) {
        obj[id] = progress;
      }
      localStorage.setItem(STORAGE_KEY_ACHIEVEMENTS, JSON.stringify(obj));
    } catch (e) {
      console.warn('Failed to save achievements:', e);
    }
  }

  onUnlock(listener: (achievement: AchievementDefinition) => void): () => void {
    this.unlockListeners.add(listener);
    return () => this.unlockListeners.delete(listener);
  }

  private tryUnlock(id: string): void {
    const progress = this.achievements.get(id);
    const def = this.definitions.get(id);
    if (!progress || !def) return;
    if (progress.unlocked) return;

    if (progress.progress >= def.maxProgress) {
      progress.unlocked = true;
      progress.unlockedAt = Date.now();
      this.saveAchievements();
      console.log(`Achievement unlocked: ${def.name}`);
      this.unlockListeners.forEach(listener => listener(def));
    }
  }

  private incrementProgress(id: string, amount: number = 1): void {
    const progress = this.achievements.get(id);
    const def = this.definitions.get(id);
    if (!progress || !def || progress.unlocked) return;

    progress.progress = Math.min(def.maxProgress, progress.progress + amount);
    this.saveAchievements();
    this.tryUnlock(id);
  }

  private setProgress(id: string, value: number): void {
    const progress = this.achievements.get(id);
    const def = this.definitions.get(id);
    if (!progress || !def || progress.unlocked) return;

    progress.progress = Math.min(def.maxProgress, value);
    this.saveAchievements();
    this.tryUnlock(id);
  }

  processEvent(event: AchievementEvent): void {
    const { type, data } = event;

    switch (type) {
      case 'spell_cast':
        this.stats.totalSpellsCast++;
        if (data.spellType) {
          this.stats.spellsCastPerType[data.spellType]++;
        }
        this.saveStats();
        break;

      case 'gesture_spell_cast':
        this.stats.totalGestureSpellsCast++;
        this.stats.totalSpellsCast++;
        if (data.spellType) {
          this.stats.spellsCastPerType[data.spellType]++;
        }
        this.saveStats();
        this.incrementProgress('gesture_mastery');
        break;

      case 'combo_triggered':
        this.stats.totalCombosTrigered++;
        this.saveStats();
        this.incrementProgress('combo_king');
        break;

      case 'shield_blocked':
        this.stats.totalShieldsBlocked++;
        this.saveStats();
        this.incrementProgress('shield_master');
        break;

      case 'match_end':
        this.stats.totalMatches++;

        if (data.won) {
          this.stats.totalWins++;
          this.stats.currentWinStreak++;
          if (this.stats.currentWinStreak > this.stats.bestWinStreak) {
            this.stats.bestWinStreak = this.stats.currentWinStreak;
          }

          if (data.wizardType) {
            this.stats.winsPerClass[data.wizardType]++;
          }

          // First Blood
          this.incrementProgress('first_blood');

          // Underdog: win with < 10 HP
          if (data.playerHealth !== undefined && data.playerHealth < 10 && data.playerHealth > 0) {
            this.incrementProgress('underdog');
          }

          // Untouchable: win without taking damage
          if (data.damageTaken !== undefined && data.damageTaken === 0) {
            this.incrementProgress('untouchable');
          }

          // Hot Streak: check current win streak
          this.setProgress('hot_streak', this.stats.currentWinStreak);

          // Class Expert: count classes with 10+ wins
          const classesWithTenWins = Object.values(this.stats.winsPerClass).filter(w => w >= 10).length;
          this.setProgress('class_expert', classesWithTenWins);
        } else {
          this.stats.totalLosses++;
          this.stats.currentWinStreak = 0;
        }

        // Dedicated: total matches
        this.setProgress('dedicated', this.stats.totalMatches);

        // Variety Show: all 5 spells in a match
        if (data.spellsCastThisMatch && data.spellsCastThisMatch.size >= 5) {
          this.incrementProgress('variety_show');
        }

        this.saveStats();
        break;
    }
  }

  getDefinitions(): AchievementDefinition[] {
    return ACHIEVEMENT_DEFINITIONS;
  }

  getProgress(id: string): AchievementProgress | undefined {
    return this.achievements.get(id);
  }

  getAllProgress(): Map<string, AchievementProgress> {
    return new Map(this.achievements);
  }

  getStats(): PlayerStats {
    return { ...this.stats };
  }

  getUnlockedCount(): number {
    let count = 0;
    for (const progress of this.achievements.values()) {
      if (progress.unlocked) count++;
    }
    return count;
  }

  getTotalCount(): number {
    return this.definitions.size;
  }
}
